import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["options", "selected", "onSelected", "label", "labelProps", "hint", "required", "disabled", "full", "sm", "size", "open", "onToggleOpen", "selectId", "name", "form", "className", "clampText", "isDark", "dark", "isTransparency", "transparencyMode", "onOpen", "data-testid", "displayPosition", "inputRef", "error", "errorText", "htmlProps", "Icon"];
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styled from 'styled-components';
import ArrowIosRight from '@crehana/icons/outline/ArrowIosRight';
import { KEY_NAMES } from "../helpers/constants";
import parseClassnames from "../helpers/parseClassnames";
import useOnClickOutside from "../hooks/useOnClickOutside";
import Menu from "../Menu";
import { baseLighter, baseMain, grayDark, white } from "../styles/colors";
import transition from "../styles/transition";
import { BACKGROUND_COLOR_CLASSNAMES, BORDER_COLOR_CLASSNAMES } from "../TextField/classnames";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var arrowStyles = {
  bottom: function bottom(open) {
    return open ? 'transform: rotate(-90deg);' : 'transform: rotate(90deg)';
  },
  top: function top(open) {
    return !open ? 'transform: rotate(-90deg);' : 'transform: rotate(90deg)';
  }
};
var Arrow = /*#__PURE__*/styled(ArrowIosRight).withConfig({
  componentId: "sc-xpmbsd-0"
})(["\n  transition: ", ";\n  ", "\n"], /*#__PURE__*/transition('transform'), function (_ref) {
  var theme = _ref.theme,
    open = _ref.open,
    displayPosition = _ref.displayPosition;
  return displayPosition === 'responsive' ? "\n        ".concat(arrowStyles.top(open), ";\n        ").concat(theme.breakpoints.up('sm'), " {\n          ").concat(arrowStyles.bottom(open), ";\n        }\n      ") : arrowStyles[displayPosition](open);
});
function Dropdown(_ref2) {
  var options = _ref2.options,
    selected = _ref2.selected,
    onSelected = _ref2.onSelected,
    label = _ref2.label,
    _ref2$labelProps = _ref2.labelProps,
    labelProps = _ref2$labelProps === void 0 ? {} : _ref2$labelProps,
    hint = _ref2.hint,
    required = _ref2.required,
    disabled = _ref2.disabled,
    full = _ref2.full,
    sm = _ref2.sm,
    _ref2$size = _ref2.size,
    size = _ref2$size === void 0 ? 4 : _ref2$size,
    openProp = _ref2.open,
    onToggleOpenProp = _ref2.onToggleOpen,
    selectId = _ref2.selectId,
    name = _ref2.name,
    form = _ref2.form,
    _ref2$className = _ref2.className,
    className = _ref2$className === void 0 ? '' : _ref2$className,
    _ref2$clampText = _ref2.clampText,
    clampText = _ref2$clampText === void 0 ? true : _ref2$clampText,
    isDark = _ref2.isDark,
    dark = _ref2.dark,
    isTransparency = _ref2.isTransparency,
    transparencyMode = _ref2.transparencyMode,
    onOpen = _ref2.onOpen,
    dataTestId = _ref2['data-testid'],
    _ref2$displayPosition = _ref2.displayPosition,
    displayPosition = _ref2$displayPosition === void 0 ? 'bottom' : _ref2$displayPosition,
    inputRef = _ref2.inputRef,
    error = _ref2.error,
    errorText = _ref2.errorText,
    htmlProps = _ref2.htmlProps,
    Icon = _ref2.Icon,
    other = _objectWithoutProperties(_ref2, _excluded);
  var OPTION_HEIGHT = sm ? 34 : 56;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    openState = _useState2[0],
    setOpenState = _useState2[1];
  var containerRef = useRef(null);
  var setSelectElementValue = function setSelectElementValue(newValue) {
    if (selectId) {
      var selectElement = document.getElementById(selectId);
      if (selectElement) {
        selectElement.value = newValue;
      }
    } else if (name) {
      var namedElements = Array.from(document.getElementsByName(name));
      var element = namedElements.find(function (e) {
        return e.tagName === 'SELECT';
      });
      if (element) {
        element.value = newValue;
      }
    }
  };
  useEffect(function () {
    if ((selectId || name) && (typeof (selected === null || selected === void 0 ? void 0 : selected.value) === 'string' || typeof (selected === null || selected === void 0 ? void 0 : selected.value) === 'number')) {
      setSelectElementValue(selected.value.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var hasOpenProp = typeof openProp === 'boolean';
  var onToggleOpen = function onToggleOpen() {
    if (!disabled) {
      var newOpenState = !openState;
      if (!hasOpenProp) {
        setOpenState(newOpenState);
        if (newOpenState && onOpen) {
          onOpen();
        }
      }
      if (typeof onToggleOpenProp === 'function') {
        onToggleOpenProp(!hasOpenProp ? newOpenState : undefined);
      }
    }
  };
  var handler = function handler() {
    if (openState || openProp) {
      onToggleOpen();
    }
  };
  useOnClickOutside(containerRef, handler);
  var onSelect = function onSelect(option) {
    var currentlySelected = selected && selected.value === option.value;
    var newSelected = option;
    if (!required && currentlySelected) {
      newSelected = undefined;
    }
    if (typeof onSelected === 'function' && selected !== newSelected) {
      onSelected(newSelected);
    }
    if (selectId || name) {
      setSelectElementValue(typeof (selected === null || selected === void 0 ? void 0 : selected.value) === 'string' || typeof (selected === null || selected === void 0 ? void 0 : selected.value) === 'number' ? selected.value.toString() : '');
    }
    onToggleOpen();
  };
  var handleKeyDown = function handleKeyDown(event, action) {
    if (event.key === KEY_NAMES.ENTER) {
      event.stopPropagation();
      action();
    }
  };
  var open = typeof openProp === 'boolean' ? openProp : openState;
  var shownOptions = options.length >= size ? size : options.length;
  var selectedLabel = (selected === null || selected === void 0 ? void 0 : selected.jsxLabel) || (selected === null || selected === void 0 ? void 0 : selected.label);
  var wrapperPositionClassNames = {
    bottom: 'flex-col',
    top: 'flex-col-reverse absolute bottom-0 inset-x-0',
    responsive: 'flex-col-reverse absolute bottom-0 sm:flex-col sm:bottom-auto sm:top-0'
  };
  var mainFontColor = isDark || dark || isTransparency || transparencyMode ? 'text-white' : 'text-base-main';
  var iconColor = isDark || dark || isTransparency || transparencyMode ? white : baseMain;
  var backgroundState = isDark || dark ? 'dark' : 'light';
  var borderState = isDark || dark ? 'dark' : 'light';
  if (isTransparency || transparencyMode) {
    backgroundState = 'transparency';
    borderState = 'transparency';
    if (openState || openProp) {
      borderState = 'transparency-focused';
      backgroundState = 'transparency-focused';
    }
    if (disabled) {
      backgroundState = 'transparency-disabled';
      borderState = 'disabled';
      mainFontColor = 'text-gray-dark';
      iconColor = grayDark;
    }
  } else {
    if (openState || openProp) {
      borderState = isDark || dark ? 'dark-focused' : 'light-focused';
    }
    if (disabled) {
      backgroundState = isDark || dark ? 'dark-disabled' : 'light-disabled';
      borderState = 'disabled';
      mainFontColor = 'text-gray-dark';
      iconColor = grayDark;
    }
  }
  if (error || errorText) {
    borderState = 'error';
  }
  if (!selectedLabel) {
    mainFontColor = isDark || dark || isTransparency || transparencyMode ? 'text-base-lighter' : 'text-gray-dark';
    iconColor = isDark || dark || isTransparency || transparencyMode ? baseLighter : grayDark;
  }
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({
    className: parseClassnames(className, cn('inline-flex flex-col', full ? 'w-full' : 'w-full sm:w-280')),
    ref: containerRef
  }, other), htmlProps), {}, {
    children: [label && /*#__PURE__*/_jsx("label", _objectSpread(_objectSpread({
      className: cn('font-subtitle3 mb-8', isDark || dark || isTransparency || transparencyMode ? 'text-white' : 'text-base-main')
    }, labelProps), {}, {
      children: label
    })), /*#__PURE__*/_jsxs("select", {
      className: "hidden",
      name: name,
      id: selectId,
      form: form,
      "data-testid": dataTestId,
      children: [hint && /*#__PURE__*/_jsx("option", {
        value: "",
        disabled: true,
        children: hint
      }), options.map(function (opt) {
        return /*#__PURE__*/_jsx("option", {
          value: typeof opt.value === 'string' || typeof opt.value === 'number' ? opt.value : "".concat(selectId || name, "-").concat(opt.value),
          children: opt.label
        }, "".concat(selectId || name, "-").concat(opt.value));
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: cn('relative', sm ? 'h-32' : 'h-56'),
      children: /*#__PURE__*/_jsxs("div", {
        className: cn('inline-flex w-full z-1', sm ? 'h-32' : 'h-56', disabled ? 'cursor-default' : 'cursor-pointer', wrapperPositionClassNames[displayPosition], {
          'shadow-dropdown': open && !(isDark || dark || isTransparency || transparencyMode)
        }),
        style: {
          transition: transition(['box-shadow', 'background-color', 'border']),
          height: "".concat((open ? OPTION_HEIGHT * (shownOptions + 1) : OPTION_HEIGHT) + (!(isDark || dark || isTransparency || transparencyMode) ? 2 : 0), "px"),
          zIndex: open ? 200 : 2
        },
        children: [/*#__PURE__*/_jsxs("div", {
          className: cn('inline-flex flex-shrink-0 justify-between items-center w-full rounded-5 border border-solid px-16', sm ? 'h-32' : 'h-56', BACKGROUND_COLOR_CLASSNAMES[backgroundState], BORDER_COLOR_CLASSNAMES[borderState]),
          onClick: onToggleOpen,
          onKeyDown: function onKeyDown(e) {
            return handleKeyDown(e, onToggleOpen);
          },
          "data-testid": dataTestId ? "".concat(dataTestId, "-button") : undefined,
          children: [/*#__PURE__*/_jsxs("div", {
            className: "flex flex-row items-center w-full",
            children: [Icon && /*#__PURE__*/_jsx(Icon, {
              size: 16,
              color: iconColor,
              className: "mr-12"
            }), /*#__PURE__*/_jsx("span", {
              className: cn('font-body3 overflow-hidden pr-16', mainFontColor, {
                'truncate break-all': sm || clampText
              }),
              "data-testid": dataTestId ? "".concat(dataTestId, "-label") : undefined,
              children: selectedLabel || hint
            })]
          }), /*#__PURE__*/_jsx(Arrow, {
            className: cn('fill-current', _defineProperty(_defineProperty({}, isDark || dark || isTransparency || transparencyMode ? 'text-secondary-main' : 'text-primary-main', !disabled), mainFontColor, disabled)),
            ignoreColor: true,
            size: 24,
            displayPosition: displayPosition,
            open: open
          })]
        }), open && /*#__PURE__*/_jsx(Menu, {
          className: "z-7",
          options: options,
          selected: selected,
          onSelected: onSelect,
          full: true,
          clampText: clampText,
          isDark: isDark || dark,
          isTransparency: isTransparency || transparencyMode,
          "data-testid": dataTestId ? "".concat(dataTestId, "-options") : undefined
        })]
      })
    }), errorText && /*#__PURE__*/_jsx("span", {
      className: cn('font-caption mt-8', disabled ? 'text-gray-dark' : 'text-red-main'),
      role: "alert",
      children: errorText
    })]
  }));
}
export default Dropdown;